/**
 * Right before checkout, only the LAST product in the cart should keep
 * Provider ID / Patient ID / Clinic ID / Practice ID as a visible line item
 * property (same rule as the Prescription Builder / Rx Cart flow).
 *
 * buy-buttons.liquid sets these 4 properties on every product independently
 * as it's added (referral-link session), so a customer who adds several
 * products across separate product-page visits ends up with every one of
 * them carrying its own copy. Trying to police that on every individual
 * add-to-cart (across however many separate page loads the customer makes
 * before checking out) turned out to be unreliable. Instead, this runs
 * once, here, at the single choke point every cart path funnels through:
 * the "Checkout" button - on both the cart drawer and the standalone /cart
 * page, since both use a plain <form method="post"> with a
 * name="checkout" submit button (no custom element intercepts it), just
 * two different form ids.
 */
(function () {
  var PROP_KEYS = ['Provider ID', 'Patient ID', 'Clinic ID', 'Practice ID'];

  function hasOurProps(props) {
    if (!props) return false;
    return PROP_KEYS.some(function (k) { return props[k]; });
  }

  // Keeps any OTHER property intact - only our 4 keys are cleared. Returns
  // null when nothing is left, so the re-add call can omit `properties`
  // entirely instead of sending `{}`.
  function stripOurProps(props) {
    var out = {};
    var any = false;
    Object.keys(props || {}).forEach(function (k) {
      if (PROP_KEYS.indexOf(k) === -1 && props[k]) { out[k] = props[k]; any = true; }
    });
    return any ? out : null;
  }

  function removeLine(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(function (r) {
      if (!r.ok) throw new Error('remove line failed: HTTP ' + r.status);
      return r.json();
    });
  }

  function addLine(variantId, quantity, properties) {
    var item = { id: variantId, quantity: quantity };
    if (properties) item.properties = properties;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify({ items: [item] })
    }).then(function (r) {
      return r.json().then(function (json) {
        if (!r.ok) throw json; // Shopify's error payload: { description, message, ... }
        return json;
      });
    });
  }

  // Shopify's cart.items array order is the order products were added
  // (oldest first) - the last entry is the most recently added product.
  // Everything else that still carries our 4 keys gets them stripped
  // (remove + re-add, sequential so lines never collide mid-flight - the
  // cart API has no "edit properties in place" call).
  // The physician-link session (localStorage "selected_physician", written by
  // layout/theme.liquid's referral decoder) as the same 4 property values
  // buy-buttons.liquid puts on a product. null without a session.
  function sessionProps() {
    var s;
    try { s = JSON.parse(localStorage.getItem('selected_physician') || 'null'); } catch (e) { return null; }
    if (!s || !s.providerTag) return null;
    function strip(tag, prefix) { return String(tag || '').replace(prefix, '').trim(); }
    var custId = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.page && ShopifyAnalytics.meta.page.customerId;
    var patient = custId ? ('SHOP-' + custId) : '';
    if (!patient) { try { patient = localStorage.getItem('anon_patient_id') || ''; } catch (e) {} }
    var props = {};
    props['Provider ID'] = strip(s.providerTag, 'provider_id:');
    if (patient) props['Patient ID'] = patient;
    if (s.clinicTag) props['Clinic ID'] = strip(s.clinicTag, 'clinic_id:');
    if (s.practiceTag) props['Practice ID'] = strip(s.practiceTag, 'practice_id:');
    return props;
  }

  // Shopify's own "Buy again" (customer account -> order -> Buy again, lands
  // on /cart?cart_link_id=...) rebuilds the cart with products only - no line
  // properties - so no line carries the IDs at all. Put them back on the last
  // line from the physician session. /cart/change.js with `properties` edits
  // that line where it is, so it stays the last line.
  function restoreOnLast(cart) {
    if (cart.items.some(function (it) { return hasOurProps(it.properties); })) return Promise.resolve(0);
    var props = sessionProps();
    if (!props) return Promise.resolve(0);
    var last = cart.items[cart.items.length - 1];
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify({
        line: cart.items.length,
        quantity: last.quantity,
        properties: Object.assign({}, last.properties || {}, props)
      })
    }).then(function (r) {
      if (!r.ok) throw new Error('restore line properties failed: HTTP ' + r.status);
      return 1;
    });
  }

  // Resolves to how many lines were changed (0 = cart left untouched).
  function stripAllButLast() {
    return fetch('/cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); }).then(function (cart) {
      if (!cart.items.length) return 0;
      if (!cart.items.some(function (it) { return hasOurProps(it.properties); })) return restoreOnLast(cart);
      var lastKey = cart.items[cart.items.length - 1].key;
      var toClean = cart.items.filter(function (it) { return it.key !== lastKey && hasOurProps(it.properties); });
      var chain = Promise.resolve();
      toClean.forEach(function (it) {
        chain = chain.then(function () {
          return removeLine(it.key).then(function () {
            return addLine(it.variant_id, it.quantity, stripOurProps(it.properties));
          });
        });
      });
      return chain.then(function () { return toClean.length; });
    });
  }

  // Both cart forms post every quantity box as positional updates[] -
  // Shopify applies them by line index. A re-added line moves to the end of
  // the cart, so after a cleanup those positions point at the wrong lines
  // and would swap quantities between products. The boxes are already
  // saved over AJAX as they change, so leaving them out loses nothing.
  function setQtyInputsDisabled(form, disabled) {
    Array.prototype.forEach.call(form.elements, function (el) {
      if (el.name === 'updates[]') el.disabled = disabled;
    });
  }

  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!(form instanceof HTMLFormElement)) return;
    var submitter = evt.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'checkout') return;

    if (form.__rxCheckoutCleanupDone) { form.__rxCheckoutCleanupDone = false; return; } // 2nd pass: let the real submit through

    evt.preventDefault();
    submitter.disabled = true;

    stripAllButLast()
      .catch(function (err) { console.error('rx checkout line-property cleanup failed:', err); return 0; })
      .then(function (reAdded) {
        form.__rxCheckoutCleanupDone = true;
        submitter.disabled = false;
        if (reAdded) setQtyInputsDisabled(form, true);
        form.requestSubmit(submitter); // re-fires 'submit'; the flag above lets it straight through this time
        // The form data is already captured by now - re-enable so a
        // back-navigation (bfcache) doesn't return to dead quantity boxes.
        if (reAdded) setQtyInputsDisabled(form, false);
      });
  }, true); // capture phase - runs regardless of any other listener on this form
})();